/**
 * pRI LLM Settings Hooks
 * Epic E-PRI-3: pRI LLM Beállítások Admin Felület
 *
 * TanStack Query hooks for pRI LLM config management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PriLlmConfig, UpdatePriLlmConfigInput } from '../api/pri-settings';
import {
  getPriSettings,
  getPriUsageStats,
  testPriConnection,
  updatePriSettings,
} from '../api/pri-settings';

const PRI_SETTINGS_KEY = ['pri', 'settings'] as const;
const PRI_USAGE_KEY = ['pri', 'usage'] as const;

export function usePriSettings() {
  const query = useQuery({
    queryKey: PRI_SETTINGS_KEY,
    queryFn: async () => {
      const res = await getPriSettings();
      return res.data;
    },
  });

  return {
    config: query.data as PriLlmConfig | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function usePriSettingsMutations() {
  const queryClient = useQueryClient();

  const update = useMutation({
    mutationFn: (data: UpdatePriLlmConfigInput) => updatePriSettings(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PRI_SETTINGS_KEY });
    },
  });

  const test = useMutation({
    mutationFn: () => testPriConnection(),
  });

  return { update, test };
}

export function usePriUsageStats() {
  const query = useQuery({
    queryKey: PRI_USAGE_KEY,
    queryFn: async () => {
      const res = await getPriUsageStats();
      return res.data;
    },
  });

  return {
    usage: query.data,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
  };
}
